import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Sun } from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const GROUP_LABELS = { entity_admin: 'Entity Admins' };

export const DayStarterConfig = ({ bare = false } = {}) => {
  const [rows, setRows] = useState([]);
  const [supportedGroups, setSupportedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/day-starter/config`, { withCredentials: true, headers: authHeaders() });
      setRows(data?.entities || []);
      setSupportedGroups(data?.supported_groups || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load Day Starter config'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggle = async (row, group, value) => {
    const key = `${row.entity_id}:${group}`;
    setSavingKey(key);
    const groups = { ...row.groups, [group]: value };
    try {
      await axios.put(
        `${API_URL}/api/day-starter/config/${row.entity_id}`,
        { groups },
        { withCredentials: true, headers: authHeaders() },
      );
      setRows(rs => rs.map(r => (r.entity_id === row.entity_id ? { ...r, groups } : r)));
      toast.success(`${GROUP_LABELS[group] || group} popup ${value ? 'enabled' : 'disabled'} for ${row.entity_name}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update'));
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div className={bare ? '' : 'bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6 mt-6'} data-testid="day-starter-config">
      {!bare && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <Sun size={20} weight="fill" className="text-[#F5C542]" />
            <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Day Starter — Popup Groups
            </h2>
          </div>
          <p className="text-sm text-[#78716C] mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Choose which user groups see the first-login-of-the-day analytics popup. The Day Starter module must also be enabled for the entity in the matrix above.
          </p>
        </>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-[#78716C]" data-testid="day-starter-config-loading">Loading…</div>
      ) : (
        <div className="border border-[#EDE6D8] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FCFAF6] text-left">
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Entity</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-32" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Module</th>
                {supportedGroups.map(g => (
                  <th key={g} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-40" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {GROUP_LABELS[g] || g}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EDE6D8]">
              {rows.map(row => (
                <tr key={row.entity_id} className="hover:bg-[#FCFAF6]/60" data-testid={`day-starter-row-${row.entity_id}`}>
                  <td className="px-4 py-3 font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{row.entity_name}</td>
                  <td className="px-4 py-3">
                    {row.module_enabled ? (
                      <Badge className="bg-[#E7F5EC] text-[#166534] hover:bg-[#E7F5EC] border-0">Enabled</Badge>
                    ) : (
                      <Badge className="bg-[#F5F5F4] text-[#78716C] hover:bg-[#F5F5F4] border-0">Off</Badge>
                    )}
                  </td>
                  {supportedGroups.map(g => (
                    <td key={g} className="px-4 py-3">
                      <Switch
                        checked={!!row.groups?.[g]}
                        disabled={savingKey === `${row.entity_id}:${g}`}
                        onCheckedChange={(v) => toggle(row, g, v)}
                        data-testid={`day-starter-toggle-${row.entity_id}-${g}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
